/**
 * Runtime configuration for the Web_Dashboard SPA.
 *
 * The SPA is a single built artifact that must be deployable to any environment, so it carries NO
 * hardcoded endpoints. Instead it fetches `/config.json` at startup — a small document the deploy
 * pipeline uploads to the S3 origin alongside the built assets, populated from the DashboardStack
 * outputs (see packages/web/README.md for the exact output→field mapping). `public/config.example.json`
 * documents the shape for local development (copy it to `public/config.json`).
 */

/** The runtime configuration the SPA needs to reach the API and authenticate against Cognito. */
export interface RuntimeConfig {
  /** Base URL of the account-scoped Soroush_API the SPA calls with the user's JWT. */
  apiEndpoint: string;
  /** Cognito OIDC issuer/authority — `https://cognito-idp.<region>.amazonaws.com/<userPoolId>`. */
  cognitoAuthority: string;
  /** Cognito app client id — the OAuth `client_id` / JWT audience the SPA obtains tokens for. */
  cognitoClientId: string;
  /** AWS region of the Cognito User Pool. */
  cognitoRegion: string;
  /** OAuth redirect URI — the SPA origin Cognito returns to after login (must be an allowed callback URL). */
  redirectUri: string;
  /**
   * The Global_Locations this deployment runs Checks from (Req 8.2) — the only locations the Check
   * form offers.
   *
   * A **deployment fact**, not a domain one: which regions exist depends on how many runner planes the
   * IaC provisioned, so it arrives here from the `SoroushApi → RunnerRegions` output rather than being
   * hardcoded. Hardcoding it would make the picker silently wrong the moment a second region deployed —
   * the same class of bug as the location field it replaces, where a plausible region name produced a
   * Check that never ran.
   *
   * Parsed from the output's comma-separated string into an array here, so every consumer gets a list.
   */
  runnerRegions: readonly string[];
}

/**
 * The locations assumed when `config.json` carries none.
 *
 * Mirrors the single runner region the platform provisions today, and exists so a `config.json` written
 * before the `RunnerRegions` output existed still yields a usable Check form rather than an empty
 * picker. The server validates the choice regardless, so a stale default is refused rather than
 * silently accepted — which is what makes a fallback safe here at all.
 */
export const DEFAULT_RUNNER_REGIONS: readonly string[] = ['eu-central-1'];

/** Split the comma-separated `RunnerRegions` output; blank and whitespace-only entries dropped. */
function parseRunnerRegions(raw: unknown): readonly string[] | undefined {
  if (Array.isArray(raw)) {
    const entries = raw.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
    return entries.length > 0 ? entries.map((entry) => entry.trim()) : undefined;
  }
  if (typeof raw !== 'string') return undefined;
  const entries = raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  return entries.length > 0 ? entries : undefined;
}

const REQUIRED_STRING_FIELDS = [
  'apiEndpoint',
  'cognitoAuthority',
  'cognitoClientId',
  'cognitoRegion',
] as const;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Validate and normalize a raw `/config.json` payload into a {@link RuntimeConfig}.
 *
 * `redirectUri` is optional in the file: when absent it defaults to the current window origin, so a
 * deployment that always logs back into its own origin does not have to hardcode the CloudFront
 * domain in the uploaded config. Every other field is required and must be a non-empty string —
 * a misconfigured deployment fails loudly rather than silently pointing at nothing.
 *
 * Pure and side-effect-free (aside from reading `defaultRedirectUri`), so it is unit-testable
 * without a network or a browser.
 */
export function normalizeRuntimeConfig(raw: unknown, defaultRedirectUri?: string): RuntimeConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('config.json must be a JSON object');
  }
  const record = raw as Record<string, unknown>;

  const missing = REQUIRED_STRING_FIELDS.filter((field) => !isNonEmptyString(record[field]));
  if (missing.length > 0) {
    throw new Error(`config.json is missing required field(s): ${missing.join(', ')}`);
  }

  const redirectUri = isNonEmptyString(record.redirectUri) ? record.redirectUri : defaultRedirectUri;
  if (!isNonEmptyString(redirectUri)) {
    throw new Error('config.json is missing "redirectUri" and no default origin is available');
  }

  return {
    apiEndpoint: record.apiEndpoint as string,
    cognitoAuthority: record.cognitoAuthority as string,
    cognitoClientId: record.cognitoClientId as string,
    cognitoRegion: record.cognitoRegion as string,
    redirectUri,
    // Optional in the file for the reason `redirectUri` is: a deployment published before the
    // `RunnerRegions` output existed must still load. Never an empty array — an empty picker is a
    // worse failure than a stale default, and the server refuses a wrong choice either way.
    runnerRegions: parseRunnerRegions(record.runnerRegions) ?? DEFAULT_RUNNER_REGIONS,
  };
}

/**
 * Fetch and normalize the deployed `/config.json`. Injectable `fetchImpl` / `defaultRedirectUri`
 * keep it testable without globals.
 */
export async function loadRuntimeConfig(
  fetchImpl: typeof fetch = fetch,
  defaultRedirectUri: string | undefined = typeof window !== 'undefined'
    ? window.location.origin + '/'
    : undefined,
): Promise<RuntimeConfig> {
  const response = await fetchImpl('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load /config.json (HTTP ${response.status})`);
  }
  const raw: unknown = await response.json();
  return normalizeRuntimeConfig(raw, defaultRedirectUri);
}
